import React from "react";
import {
  FiUsers,
  FiGrid,
  FiGlobe,
  FiCreditCard,
  FiCloud,
  FiCpu,
} from "react-icons/fi";
import "./ProductLauncher.css";

type ProductItem = {
  title: string;
  description: string;
  route: string;
  accentClass: string;
  icon: React.ReactNode;
};

type LauncherSideItem = {
  title: string;
  description?: string;
  link?: string;
};

type LauncherProps = {
  items?: ProductItem[];
  variant?: "launcher" | "mega";
  sideTitle?: string;
  sideItems?: LauncherSideItem[];
  viewAllHref?: string;
  viewAllLabel?: string;
  viewAllSubLabel?: string;
};

const defaultProducts: ProductItem[] = [
  {
    title: "Connect",
    description: "Acquire and engage founders and partners at scale.",
    route: "/products/connect",
    accentClass: "accent-blue",
    icon: <FiUsers size={22} />,
  },
  {
    title: "Edge",
    description: "Workspace for docs, incorporation, grants, schemes and requests.",
    route: "/products/edge",
    accentClass: "accent-green",
    icon: <FiGrid size={22} />,
  },
  {
    title: "Discovery",
    description: "Find leads, buyers, suppliers and opportunities.",
    route: "/products/discovery",
    accentClass: "accent-orange",
    icon: <FiGlobe size={22} />,
  },
  {
    title: "Payments",
    description: "Collect, reconcile and manage payments.",
    route: "/products/payments",
    accentClass: "accent-purple",
    icon: <FiCreditCard size={22} />,
  },
  {
    title: "Axo Cloud",
    description: "Host, secure and scale digital operations.",
    route: "/products/cloud",
    accentClass: "accent-cyan",
    icon: <FiCloud size={22} />,
  },
  {
    title: "AI Agents",
    description: "Autonomous and assisted AI employees for your business.",
    route: "/products/ai-agents",
    accentClass: "accent-pink",
    icon: <FiCpu size={22} />,
  },
];

export const ProductLauncher: React.FC<LauncherProps> = ({
  items = defaultProducts,
  variant = "launcher",
  sideTitle = "Platform Advantage",
  sideItems = [
    { title: "Unified Business Identity", description: "One identity, everywhere." },
    { title: "Seamless Integrations", description: "Connect the tools you already use." },
    { title: "AI-Powered Automation", description: "Automate workflows and decisions." },
    { title: "Enterprise-Grade Security", description: "Built-in security by design." },
    { title: "Scalable Cloud Infrastructure", description: "Reliable, secure and future-ready." },
  ],
  viewAllHref = "/products",
  viewAllLabel = "View all products",
  viewAllSubLabel = "Browse the full suite",
}) => {
  if (variant === "mega") {
    return (
      <div className="launcher-panel launcher-panel-legacy">
        <div className="launcher-main">
          <div className="launcher-grid launcher-grid-legacy">
            {items.map((product) => (
              <a key={product.title} href={product.route} className="launcher-card launcher-card-legacy">
                <span className={`launcher-card-icon ${product.accentClass}`}>{product.icon}</span>
                <span className="launcher-card-copy">
                  <span className="launcher-card-title">{product.title}</span>
                  <span className="launcher-card-desc">{product.description}</span>
                </span>
              </a>
            ))}
          </div>
        </div>

        <aside className="launcher-side">
          <h3>{sideTitle}</h3>
          <ul>
            {sideItems.map((item) => (
              <li key={item.title}>
                {item.link ? (
                  <a href={item.link}>
                    <strong>{item.title}</strong>
                  </a>
                ) : (
                  <strong>{item.title}</strong>
                )}
                {item.description ? <span>{item.description}</span> : null}
              </li>
            ))}
          </ul>
          <a href={viewAllHref} className="launcher-view-all">
            <span className="launcher-view-all-copy">
              <strong>{viewAllLabel}</strong>
              <span>{viewAllSubLabel}</span>
            </span>
            <span className="launcher-view-all-arrow">→</span>
          </a>
        </aside>
      </div>
    );
  }

  return (
    <div className="launcher-panel">
      <div className="launcher-scroll">
        <a
          href="https://orbitone.live/"
          target="_blank"
          rel="noreferrer"
          className="launcher-feature"
        >
          <span className="launcher-feature-badge" aria-hidden="true">
            <span className="launcher-feature-dot" />
          </span>
          <span className="launcher-feature-copy">
            <strong>Discover Orbit One Platform</strong>
            <span>Open the Orbit One experience in a new tab.</span>
          </span>
          <span className="launcher-feature-arrow" aria-hidden="true">
            →
          </span>
        </a>

        <div className="launcher-apps-shell">
          <div className="launcher-grid">
            {items.map((product) => (
              <a
                key={product.title}
                href={product.route}
                className="launcher-card"
                target={product.route.startsWith("http") ? "_blank" : "_self"}
                rel={product.route.startsWith("http") ? "noreferrer" : undefined}
              >
                <span className={`launcher-card-icon ${product.accentClass}`}>
                  {product.icon}
                </span>
                <span className="launcher-card-copy">
                  <span className="launcher-card-title">{product.title}</span>
                  <span className="launcher-card-desc">{product.description}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductLauncher;
